import { Fragment, useEffect, useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import { mdiDelete, mdiMagnify, mdiClose } from "@mdi/js";
import MdiIcon from '../mdi/MdiIcon';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChatSpinner } from './ChatSpinner';
import useDeleteDialog from '../dialog/DeleteDialog/useDeleteDialog';
import { groupConversations, type HistoryGroupId } from './historyGroups';
import type { Conversation } from './types';

interface ConversationListProps {
  conversations: Conversation[];
  currentId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  /** The current filter, so the box can show what is applied. */
  search: string;
  onSearch: (term: string) => void;
  /** Whether another page is waiting to be asked for. */
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
}

/** How long to wait after the last keystroke before querying. */
const SEARCH_DEBOUNCE_MS = 300;

export function ConversationList({
  conversations,
  currentId,
  onSelect,
  onDelete,
  search,
  onSearch,
  hasMore,
  loading,
  onLoadMore,
}: ConversationListProps) {
  const { openDialog } = useDeleteDialog();

  // Held locally so the box stays responsive, then handed up once typing pauses.
  // Querying per keystroke would restart the list under the cursor on every
  // character.
  const [term, setTerm] = useState(search);

  // Adopt a filter set elsewhere, e.g. cleared by "New conversation".
  useEffect(() => {
    setTerm(search);
  }, [search]);

  useEffect(() => {
    if (term === search) return;

    const timer = setTimeout(() => onSearch(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term, search, onSearch]);

  // Deleting a conversation is irreversible and used to happen on a single
  // click, so confirm through the shared DeleteDialog first.
  const confirmDelete = (convId: string) => {
    openDialog(() => onDelete(convId));
  };

  /**
   * The heading of a group of conversations.
   *
   * A function rather than a constant: the macro compiles to a call that
   * needs an active locale, and a constant would run it while the module is
   * being imported -- before any locale has been activated.
   */
  const groupLabel = (id: HistoryGroupId): string => {
    switch (id) {
      case 'today':
        return t`Today`;
      case 'yesterday':
        return t`Yesterday`;
      case 'lastWeek':
        return t`Last 7 days`;
      case 'older':
        return t`Older`;
    }
  };

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  const formatDay = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  /**
   * What a row says about when it was last touched.
   *
   * Inside the "today" and "yesterday" groups the heading already says which
   * day it is, so repeating the date under every row is noise. Older rows keep
   * their date, because there the heading is too coarse to tell two of them
   * apart.
   */
  const formatWhen = (group: HistoryGroupId, dateStr: string) => {
    if (group === 'today' || group === 'yesterday') return formatTime(dateStr);
    return `${formatDay(dateStr)} ${formatTime(dateStr)}`;
  };

  // Declared once and used by both the empty and populated branches: a filtered
  // list that matches nothing still has to offer the box that filtered it, or
  // the user is stuck with results they cannot see the filter for.
  const clearSearch = () => {
    setTerm('');
    onSearch('');
  };

  const searchField = (
    <TextField
      fullWidth
      size="small"
      placeholder={t`Search history...`}
      value={term}
      onChange={(e) => setTerm(e.target.value)}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <MdiIcon d={mdiMagnify} />
            </InputAdornment>
          ),
          endAdornment: term ? (
            <InputAdornment position="end">
              <Tooltip title={t`Clear search`}>
                <IconButton size="small" aria-label={t`Clear search`} onClick={clearSearch}>
                  <MdiIcon d={mdiClose} size={16} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null,
        },
      }}
    />
  );

  if (conversations.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        {searchField}
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 2 }}>
          {search ? (
            <Trans>No conversations match "{search}"</Trans>
          ) : (
            <Trans>No conversations yet</Trans>
          )}
        </Typography>
      </Box>
    );
  }

  const groups = groupConversations(conversations);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ px: 1, pt: 1, pb: 0.5, flexShrink: 0 }}>{searchField}</Box>

      {/*
        The list scrolls, the search box does not.

        The box used to be sticky inside the scrolling panel, which put it
        behind the group headings as they passed under it. Keeping it out of
        the scroll area entirely is simpler and it stays put.
      */}
      <Box sx={{ overflow: 'auto', minHeight: 0, flex: 1 }}>
        <List dense sx={{ py: 0 }}>
          {groups.map((group) => (
            <Fragment key={group.id}>
              <ListSubheader
                data-testid="chat-history-group"
                sx={{
                  // The default 48px is taller than a dense row, and the
                  // heading is a divider rather than a section title.
                  lineHeight: '32px',
                  py: 0,
                  px: 2,
                  zIndex: 2,
                }}
              >
                {groupLabel(group.id)}
              </ListSubheader>

              {group.conversations.map((conv) => (
                <ListItem
                  key={conv.id}
                  data-testid="chat-history-row"
                  disablePadding
                  // The delete control is a sibling of the row, not a child
                  // of it. Inside the row it was a button within a button,
                  // which is invalid markup and leaves the keyboard with two
                  // controls where there is one.
                  secondaryAction={
                    <Tooltip title={t`Delete`}>
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={t`Delete ${conv.title || t`New conversation`}`}
                        onClick={() => confirmDelete(conv.id)}
                      >
                        <MdiIcon d={mdiDelete} size={16} />
                      </IconButton>
                    </Tooltip>
                  }
                  sx={{ mx: 1, borderRadius: 1 }}
                >
                  <ListItemButton
                    selected={conv.id === currentId}
                    onClick={() => onSelect(conv.id)}
                    sx={{
                      borderRadius: 1,
                      py: 0.5,
                      // The full width of the row, so the selected state
                      // covers the row rather than the text in it.
                      width: '100%',
                    }}
                  >
                    <ListItemText
                      primary={conv.title || t`New conversation`}
                      secondary={formatWhen(group.id, conv.updated_at)}
                      slotProps={{
                        primary: { noWrap: true, sx: { fontSize: '0.875rem' } },
                        secondary: { sx: { fontSize: '0.75rem' } },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </Fragment>
          ))}
        </List>

        {hasMore && (
          <Box sx={{ px: 1, py: 1, textAlign: 'center' }}>
            <Button size="small" onClick={onLoadMore} disabled={loading} startIcon={loading ? <ChatSpinner size={12} /> : undefined}>
              {loading ? t`Loading...` : t`Load more`}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
